import type * as api from '@gitbook/api-client';
import type { ContentRefKind } from '@gitbook/app-content-refs';
import type { ContentReferenceContext } from '@gitbook/app-database';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import { resolveURLToNavigation } from '@gitbook/app-navigation';
import {
    BLOCK_EXPANDABLE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    getSectionsInDoc,
} from '@gitbook/doc-core';
import type { HiveClient } from '@gitbook/hive-client';
import * as hiveCore from '@gitbook/hive-core';
import {
    createPalette,
    type PaletteCommandDef,
    type PaletteDef,
    type PaletteGroupDef,
    type PalettePositionOptions,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import type { Document } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import { createCache, getSuspense } from '@gitbook/util-suspense-cache';
import { assertNever, filterOutNullable } from '@gitbook/util-ts';
import { isUrl, normalizeUrlLikeStringToUrl } from '@gitbook/util-url';

import { apiResource, readAPICache } from '../../state';
import { searchOrganizationMembers, searchSpaces } from '../Search';
import { SiteSectionGroupIcon, SiteSectionIcon } from '../Site';
import { SpaceIcon } from '../Space';
import { UserAvatar } from '../Users';
import { resolveContentRefCache, resolveScreenToContentRefCache } from './caches';

/**
 * Cache for page paths map computation, keyed by revision id
 */
const pagePathsMapCache = createCache({
    getValue: async (revision: hiveCore.Revision) => {
        return hiveCore.getAbsolutePagePathsMap(revision);
    },
    getKey: (revision: hiveCore.Revision) => revision.id,
});

type KindToContent = {
    [C in api.ContentRef as C['kind']]: C;
};

const defaultAccept: ContentRefKind[] = ['anchor', 'page', 'space', 'file', 'user', 'url'];

type CreateInputContentRefPaletteExtraGroup<T extends ContentRefKind[] = ContentRefKind[]> =
    | PaletteGroupDef
    | ((pick: (link: KindToContent[T[number]]) => any) => PaletteGroupDef);

export type CreateInputContentRefPaletteOptions<T extends ContentRefKind[] = ContentRefKind[]> = {
    /**
     * List of accepted references
     */
    accept?: T;

    /**
     * Allow customizing the search input's placeholder and value on this palette.
     * If `search` it not provided, the default placeholder will be used.
     * */
    search?: {
        enabled?: boolean;
        placeholder?: string;
        initialValue?: string;
    };

    /** Sizing / position for the palette */
    position?: PalettePositionOptions;

    /**
     * Extra groups to be prepended to the beginning of the results.
     */
    extraGroupsAtStart?: CreateInputContentRefPaletteExtraGroup<T>[];

    /**
     * Extra groups to be appended to the end of the results.
     */
    extraGroups?: CreateInputContentRefPaletteExtraGroup<T>[];

    /**
     * Value if used in an input.
     */
    value?: api.ContentRef | null | undefined;
};

/**
 * Create a command palette to pick a content reference.
 */
export function createInputContentRefPalette<T extends ContentRefKind[]>(
    pick: (link: KindToContent[T[number]]) => any,
    context: ContentReferenceContext & { document?: Document },
    hiveClient: HiveClient,
    options: CreateInputContentRefPaletteOptions<T> = {}
): PaletteDef {
    const { document, ...rawContext } = context;
    const { space } = context;
    const {
        accept = defaultAccept,
        position = {
            width: PalettePositionWidth.LARGE,
        },
        value,
    } = options;
    const defaultSearch = { enabled: true, placeholder: 'Type a URL or search in your content...' };
    const search = { ...defaultSearch, ...options.search };
    const untypedPick = pick as (link: api.ContentRef) => any;
    const groupMap = new Map<ContentRefKind, PaletteGroupDef | PaletteGroupDef[]>();

    // Get site structure palette groups, if applicable
    const siteStructureGroups: PaletteGroupDef[] = getSiteStructurePaletteGroups({
        organizationId: rawContext.organization,
        accept,
        context: rawContext,
        hiveClient,
        pick: untypedPick,
    });

    /*
     * Input a raw url.
     */
    if (accept.includes('url')) {
        groupMap.set('url', {
            key: 'url',
            commands: (query) => {
                if (!query) {
                    return [];
                }

                if (isUrl(query)) {
                    // Try resolving to a content ref if it's a GitBook.com url
                    const screen = resolveURLToNavigation(query);

                    if (screen) {
                        const [contentRef] = resolveScreenToContentRefCache.read([
                            hiveClient,
                            screen,
                            rawContext,
                        ]);
                        const [resolved] = resolveContentRefCache.read([
                            hiveClient,
                            contentRef,
                            rawContext,
                        ]);

                        if (contentRef && resolved) {
                            return [
                                {
                                    key: query,
                                    title: `Link to "${resolved.text}"`,
                                    icon: Icon.Link,
                                    trigger: () => {
                                        untypedPick(contentRef);
                                    },
                                },
                            ];
                        }
                    }
                }

                const parsedHref = normalizeUrlLikeStringToUrl(query);

                if (!parsedHref) {
                    return [];
                }

                return [
                    {
                        key: query,
                        title: `Link to "${query}"`,
                        icon: Icon.Link,
                        trigger: () => {
                            untypedPick({ kind: 'url', url: parsedHref });
                        },
                    },
                ];
            },
        });
    }

    /*
     * Search anchors in the document.
     */
    if (
        accept.includes('anchor') &&
        document &&
        // If there is no value or if the value is an anchor on the current page.
        (!value || (value.kind === 'anchor' && (!value.page || value.page === context.page)))
    ) {
        groupMap.set('anchor', {
            title: 'Current page',
            key: 'anchors',
            commands: () => getAnchorCommandsForDoc(document, {}, untypedPick),
        });
    }

    /*
     * Search in the current space.
     * Hide this if we are in the context of a site, as we will show the site structure instead
     */
    if (accept.includes('page') && context.space && !rawContext.site) {
        // First page is either the one from the value
        // or the current page from context.
        const firstPage =
            (value?.kind === 'anchor' || value?.kind === 'page') &&
            (!value.space || value.space === context.space)
                ? value.page
                : context.page;
        groupMap.set(
            'page',
            createSpacePagesGroup({
                content: {
                    spaceId: context.space,
                    changeRequestId: 'changeRequest' in context ? context.changeRequest : undefined,
                },
                hiveClient,
                pick: untypedPick,
                options: {
                    accept,
                    firstPage,
                    targetCurrentSpace: true,
                },
            })
        );
    }

    /*
     * Search for spaces.
     * Hide this if we are in the context of a site, as we will show the site structure instead
     */
    if (accept.includes('space') && !rawContext.site) {
        groupMap.set('space', {
            title: 'Spaces',
            key: 'spaces',
            commands: (query) => {
                const spaceResults = searchSpaces(query, context.organization);

                return spaceResults
                    .map((result) => ({
                        key: result.space.id,
                        icon: <SpaceIcon space={result.space} />,
                        title: result.space.title,
                        trigger: () => untypedPick({ kind: 'space', space: result.space.id }),
                        descendant: accept.includes('page')
                            ? () =>
                                  createPalette({
                                      search: { placeholder: 'Search page…' },
                                      position: {
                                          preferredPlacement: 'right',
                                          preferredAlignment: 'start',
                                          width: PalettePositionWidth.LARGE,
                                      },
                                      groups: [
                                          createSpacePagesGroup({
                                              content: {
                                                  spaceId: result.space.id,
                                                  changeRequestId: undefined,
                                              },
                                              hiveClient,
                                              pick: untypedPick,
                                              options: {
                                                  accept,
                                                  targetCurrentSpace: result.space.id === space,
                                              },
                                          }),
                                      ],
                                  })
                            : undefined,
                    }))
                    .filter(filterOutNullable);
            },
        });
    }

    /*
     * Search for users.
     */
    if (accept.includes('user')) {
        groupMap.set('user', {
            title: 'Users',
            key: 'users',
            commands: (query) => {
                const members = searchOrganizationMembers({
                    organizationId: context.organization,
                    query,
                    limit: 8,
                });

                return members.map((member) => ({
                    key: member.user.id,
                    icon: () => <UserAvatar user={member.user} size="md" tooltip={false} />,
                    title: member.user.displayName,
                    subtitle: member.user.email,
                    trigger: () => {
                        untypedPick({ kind: 'user', user: member.user.id });
                    },
                }));
            },
        });
    }

    // Order groups based on accept order
    const groups: PaletteGroupDef[] = accept.flatMap((kind) => {
        const group = groupMap.get(kind);
        return Array.isArray(group) ? group : group ? [group] : [];
    });

    // Append extra groups
    options.extraGroups?.forEach((group) =>
        groups.push(typeof group === 'function' ? group(untypedPick) : group)
    );

    // Prepend site structure groups if we are in the context of a site
    if (rawContext.site) {
        groups.unshift(...siteStructureGroups);
    }

    // Prepend extra groups
    options.extraGroupsAtStart?.forEach((group) =>
        groups.unshift(typeof group === 'function' ? group(untypedPick) : group)
    );

    return createPalette({
        search: search.enabled
            ? { placeholder: search.placeholder, initialValue: search.initialValue }
            : undefined,
        groups,
        position,
    });
}

/** Generate commands to pick an anchor in a page */
function createPageAnchorsPalette(
    spaceKey: string,
    page: hiveCore.RevisionPageDocument,
    hiveClient: HiveClient,
    pick: (link: api.ContentRefAnchor | api.ContentRefPage) => any,
    options: { targetCurrentSpace: boolean }
): PaletteDef {
    return createPalette({
        search: { placeholder: 'Search heading or tab…' },
        position: {
            preferredPlacement: 'right',
            preferredAlignment: 'middle',
            width: PalettePositionWidth.LARGE,
        },
        groups: [
            {
                key: 'page',
                commands: [
                    {
                        key: 'pick',
                        title: `Link to "${page.linkTitle || page.title}"`,
                        icon: Icon.Page,
                        trigger: () => {
                            pick({
                                kind: 'page',
                                page: page.id,
                                space: !options.targetCurrentSpace ? spaceKey : undefined,
                            });
                        },
                    },
                ],
            },
            {
                title: 'Page headings & tabs',
                key: 'anchors',
                commands: () =>
                    getAnchorCommands(
                        spaceKey,
                        page,
                        hiveClient,
                        {
                            page: page.id,
                            space: !options.targetCurrentSpace ? spaceKey : undefined,
                        },
                        pick
                    ),
            },
        ],
    });
}

/** Generate a palette group def for pages of a Space */
export function createSpacePagesGroup(props: {
    content: {
        spaceId: string;
        changeRequestId: string | undefined;
    };
    hiveClient: HiveClient;
    pick: (link: api.ContentRefAnchor | api.ContentRefPage) => any;
    options: {
        accept: ContentRefKind[];
        targetCurrentSpace: boolean;
        firstPage?: string;
        hideTitle?: boolean;
    };
}): PaletteGroupDef {
    const { content, hiveClient, pick, options } = props;
    const { accept, targetCurrentSpace, hideTitle, firstPage } = options;

    return {
        title: hideTitle ? undefined : targetCurrentSpace ? 'Pages in current space' : 'Pages',
        key: 'space',
        commands: () => {
            const sourceRevision = getSuspense(
                hiveClient.store.getBranchRevision(
                    content.spaceId,
                    content.changeRequestId
                        ? getChangeRequestBranchId(content.changeRequestId)
                        : hiveCore.CONTENT_DEFAULT_BRANCH
                )
            );
            if (!sourceRevision) {
                return [];
            }

            const { revision } = getSuspense(
                hiveClient
                    .getContentComputer(sourceRevision.contentId)
                    .getCompleteRevision(sourceRevision)
            );

            // Use cached paths for pages to avoid expensive recomputation on every render
            const paths = pagePathsMapCache.read(revision);

            const currentRevisionPage = firstPage ? revision.pages[firstPage] : null;
            const revisionPages = Object.values(revision.pages);
            const sortedPages = currentRevisionPage
                ? Array.from(new Set([currentRevisionPage, ...revisionPages]))
                : revisionPages;

            return sortedPages.reduce<PaletteCommandDef[]>((commands, page) => {
                if (page.type === hiveCore.RevisionPageType.Link) {
                    return commands;
                }

                const canPickPage = accept?.includes('page');
                const canPickAnchor = accept?.includes('anchor');

                // Only add page command if 'page' is accepted
                if (canPickPage) {
                    commands.push({
                        key: page.id,
                        title: page.linkTitle || page.title,
                        subtitle: () => paths.get(page.id) ?? '',
                        icon:
                            page.type === hiveCore.RevisionPageType.Group
                                ? Icon.AddPageGroup
                                : Icon.Page,
                        trigger: () => {
                            pick({
                                kind: 'page',
                                page: page.id,
                                space: targetCurrentSpace ? undefined : content.spaceId,
                            });
                        },
                        descendant:
                            canPickAnchor && page.type === hiveCore.RevisionPageType.Document
                                ? () => {
                                      return {
                                          ...createPageAnchorsPalette(
                                              content.spaceId,
                                              page,
                                              hiveClient,
                                              pick,
                                              {
                                                  targetCurrentSpace,
                                              }
                                          ),
                                      };
                                  }
                                : undefined,
                    });
                } else if (canPickAnchor && page.type === hiveCore.RevisionPageType.Document) {
                    // If only anchors are accepted, show anchors directly without page command
                    const anchorCommands = getAnchorCommands(
                        content.spaceId,
                        page,
                        hiveClient,
                        {
                            page: page.id,
                            space: targetCurrentSpace ? undefined : content.spaceId,
                        },
                        pick
                    );

                    commands.push(...anchorCommands);
                }

                return commands;
            }, []);
        },
    };
}

/** Generate commands to select an anchor in a page */
function getAnchorCommands(
    spaceKey: string,
    page: hiveCore.RevisionPageDocument,
    hiveClient: HiveClient,
    anchorContext: Partial<api.ContentRefAnchor>,
    pick: (link: api.ContentRefAnchor) => any
): PaletteCommandDef[] {
    const document = getSuspense(hiveClient.store.getPageDocument(spaceKey, page));
    if (!document) {
        return [];
    }

    return getAnchorCommandsForDoc(document, anchorContext, pick);
}

/** Generate commands to select an anchor in a document */
function getAnchorCommandsForDoc(
    document: Document,
    anchorContext: Partial<api.ContentRefAnchor>,
    pick: (link: api.ContentRefAnchor) => any
): PaletteCommandDef[] {
    const sections = getSectionsInDoc(document);

    return sections
        .filter((section) => !!section.anchor)
        .map((section) => {
            const icon = (() => {
                switch (section.type) {
                    case BLOCK_HEADING_1:
                        return Icon.Heading1;
                    case BLOCK_HEADING_2:
                        return Icon.Heading2;
                    case BLOCK_HEADING_3:
                        return Icon.Heading3;
                    case BLOCK_OPENAPI:
                    case BLOCK_OPENAPI_OPERATION:
                    case BLOCK_OPENAPI_SCHEMAS:
                    case BLOCK_OPENAPI_WEBHOOK:
                        return Icon.OpenAPI;
                    case BLOCK_EXPANDABLE:
                        return Icon.BlockExpandable;
                    case BLOCK_TABS_ITEM:
                    case BLOCK_TABS:
                        return Icon.Tabs;
                    case null:
                        return Icon.Hash;
                    default:
                        assertNever(section.type);
                }
            })();

            return {
                key: section.key,
                icon,
                title: section.title,
                trigger: () => {
                    pick({ kind: 'anchor', anchor: section.anchor, ...anchorContext });
                },
            };
        });
}

function getSiteStructurePaletteGroups(input: {
    organizationId: string;
    accept: ContentRefKind[];
    context: ContentReferenceContext;
    hiveClient: HiveClient;
    pick: (link: api.ContentRef) => any;
}): PaletteGroupDef[] {
    const { organizationId, accept, context, hiveClient, pick } = input;
    const siteId = context.site;

    if (
        !siteId ||
        !accept.some((kind) => kind === 'space' || kind === 'page' || kind === 'anchor')
    ) {
        return [];
    }

    return [
        {
            key: 'site-structure',
            commands: () => {
                const data = readAPICache(
                    apiResource(
                        'getSiteStructure',
                        { organizationId, siteId },
                        {
                            nullOnError: true,
                        }
                    )
                );

                if (!data) {
                    return [];
                }

                const commands = getSiteStructureCommands(data, {
                    accept,
                    context,
                    hiveClient,
                    pick,
                }).filter(filterOutNullable);

                return commands;
            },
        },
    ];
}

function getSiteStructureCommands(
    siteStructure: api.SiteStructure,
    options: SiteStructureCommandOptions
): (PaletteCommandDef | null)[] {
    switch (siteStructure.type) {
        case 'sections':
            return siteStructure.structure.map((item) =>
                item.object === 'site-section-group'
                    ? createSiteSectionGroupCommand(item, options)
                    : createSiteSectionCommand(item, options)
            );
        case 'siteSpaces':
            return siteStructure.structure.map((siteSpace) =>
                createSiteSpaceCommand(siteSpace, options)
            );
        default:
            assertNever(siteStructure);
    }
}

interface SiteStructureCommandOptions {
    accept: ContentRefKind[];
    context: ContentReferenceContext;
    hiveClient: HiveClient;
    pick: (link: api.ContentRef) => any;
}

function createSiteSectionGroupCommand(
    siteSectionGroup: api.SiteSectionGroup,
    options: SiteStructureCommandOptions
): PaletteCommandDef | null {
    const commands = siteSectionGroup.children
        .map((child) =>
            child.object === 'site-section-group'
                ? createSiteSectionGroupCommand(child, options)
                : createSiteSectionCommand(child, options)
        )
        .filter(filterOutNullable);

    if (commands.length === 0) {
        return null;
    }

    return {
        key: siteSectionGroup.id,
        title: siteSectionGroup.title,
        icon: <SiteSectionGroupIcon siteSectionGroup={siteSectionGroup} />,
        descendant: () =>
            createPalette({
                groups: [
                    {
                        key: `site-section-group-${siteSectionGroup.id}`,
                        commands,
                    },
                ],
            }),
    };
}

function createSiteSectionCommand(
    siteSection: api.SiteSection,
    options: SiteStructureCommandOptions
): PaletteCommandDef | null {
    const { accept, context, hiveClient, pick } = options;
    const canPickSpace = accept.includes('space');
    const canPickPages = accept.includes('page') || accept.includes('anchor');

    // If section has only one space, flatten it like in the navbar
    if (siteSection.siteSpaces.length === 1) {
        const singleSiteSpace = siteSection.siteSpaces[0];

        const descendant = canPickPages
            ? () =>
                  createPalette({
                      search: { placeholder: 'Search page…' },
                      position: {
                          preferredPlacement: 'right',
                          preferredAlignment: 'start',
                          width: PalettePositionWidth.LARGE,
                      },
                      groups: [
                          createSpacePagesGroup({
                              content: {
                                  spaceId: singleSiteSpace.space.id,
                                  changeRequestId:
                                      'changeRequest' in context &&
                                      context.space === singleSiteSpace.space.id
                                          ? context.changeRequest
                                          : undefined,
                              },
                              hiveClient,
                              pick: pick as (
                                  link: api.ContentRefAnchor | api.ContentRefPage
                              ) => any,
                              options: {
                                  accept,
                                  targetCurrentSpace: context.space === singleSiteSpace.space.id,
                                  hideTitle: true,
                              },
                          }),
                      ],
                  })
            : undefined;

        if (!canPickSpace && !descendant) {
            return null;
        }

        if (descendant) {
            return {
                key: siteSection.id,
                title: siteSection.title,
                icon: <SiteSectionIcon siteSection={siteSection} />,
                descendant,
                ...(canPickSpace
                    ? {
                          trigger: () => {
                              pick({ kind: 'space', space: singleSiteSpace.space.id });
                          },
                      }
                    : {}),
            } satisfies PaletteCommandDef;
        }

        if (!canPickSpace) {
            return null;
        }

        return {
            key: siteSection.id,
            title: siteSection.title,
            icon: <SiteSectionIcon siteSection={siteSection} />,
            trigger: () => {
                pick({ kind: 'space', space: singleSiteSpace.space.id });
            },
        } satisfies PaletteCommandDef;
    }

    // Multiple spaces - show variants
    const commands = siteSection.siteSpaces
        .map((siteSpace) => createSiteSpaceCommand(siteSpace, options))
        .filter(filterOutNullable);

    if (commands.length === 0) {
        return null;
    }

    return {
        key: siteSection.id,
        title: siteSection.title,
        icon: <SiteSectionIcon siteSection={siteSection} />,
        descendant: () =>
            createPalette({
                groups: [
                    {
                        key: `site-section-${siteSection.id}`,
                        commands,
                    },
                ],
            }),
    };
}

function createSiteSpaceCommand(
    siteSpace: api.SiteSpace,
    options: SiteStructureCommandOptions
): PaletteCommandDef | null {
    const { accept, context, hiveClient, pick } = options;
    const canPickSpace = accept.includes('space');
    const canPickPages = accept.includes('page') || accept.includes('anchor');

    const descendant = canPickPages
        ? () =>
              createPalette({
                  search: { placeholder: 'Search page…' },
                  position: {
                      preferredPlacement: 'right',
                      preferredAlignment: 'start',
                      width: PalettePositionWidth.LARGE,
                  },
                  groups: [
                      createSpacePagesGroup({
                          content: {
                              spaceId: siteSpace.space.id,
                              changeRequestId:
                                  'changeRequest' in context && context.space === siteSpace.space.id
                                      ? context.changeRequest
                                      : undefined,
                          },
                          hiveClient,
                          pick: pick as (link: api.ContentRefAnchor | api.ContentRefPage) => any,
                          options: {
                              accept,
                              targetCurrentSpace: context.space === siteSpace.space.id,
                              hideTitle: true,
                          },
                      }),
                  ],
              })
        : undefined;

    if (!canPickSpace && !descendant) {
        return null;
    }

    if (descendant) {
        return {
            key: siteSpace.id,
            title: siteSpace.title,
            icon: <SpaceIcon space={siteSpace.space} />,
            descendant,
            ...(canPickSpace
                ? {
                      trigger: () => {
                          pick({ kind: 'space', space: siteSpace.space.id });
                      },
                  }
                : {}),
        } satisfies PaletteCommandDef;
    }

    if (!canPickSpace) {
        return null;
    }

    return {
        key: siteSpace.id,
        title: siteSpace.title,
        icon: <SpaceIcon space={siteSpace.space} />,
        trigger: () => {
            pick({ kind: 'space', space: siteSpace.space.id });
        },
    } satisfies PaletteCommandDef;
}
